import { createFileRoute } from '@tanstack/react-router'
import { LegalLayout } from '@/components/layout/legal-layout'
import { breadcrumbSchema, seo } from '@/shared/lib/seo'

export const Route = createFileRoute('/cookies')({
  head: () => ({
    ...seo({
      title: 'Cookie Policy | ControlStack.ai',
      description:
        'Which cookies controlstack.ai sets, what each one is for, how long it lasts, and how to change your preferences at any time.',
      path: '/cookies',
      jsonLd: [breadcrumbSchema('Cookie Policy', '/cookies')],
    }),
  }),
  component: CookiePolicy,
})

function CookiePolicy() {
  return (
    <LegalLayout title="Cookie Policy" lastUpdated="January 12, 2025">
      <section>
        <h2>1. What Are Cookies</h2>
        <p>
          Cookies are small text files that are stored on your device when you
          visit a website. They help websites remember your preferences and
          improve your browsing experience. Cookies can be "session" cookies
          (deleted when you close your browser) or "persistent" cookies (remain
          until they expire or you delete them).
        </p>
      </section>

      <section>
        <h2>2. How We Use Cookies</h2>
        <p>controlstack.ai uses cookies for the following purposes:</p>
        <ul>
          <li>To remember your preferences and settings</li>
          <li>To keep you signed in to your account</li>
          <li>To understand how you use our website</li>
          <li>To improve our services based on usage data</li>
          <li>To deliver relevant content and advertisements</li>
        </ul>
      </section>

      <section>
        <h2>3. Types of Cookies We Use</h2>

        <div className="space-y-8 not-prose">
          <div className="bg-white/[0.03] border border-white/10 p-6 rounded-2xl">
            <h3 className="text-lg font-bold text-white mb-4">
              Necessary Cookies
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              These cookies are essential for the website to function properly.
              They cannot be disabled.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-white/10 text-white font-semibold">
                    <th className="py-3 px-2">Cookie</th>
                    <th className="py-3 px-2">Purpose</th>
                    <th className="py-3 px-2">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-slate-400">
                  <tr className="border-b border-white/5">
                    <td className="py-3 px-2">cookie-consent</td>
                    <td className="py-3 px-2">
                      Stores your cookie preferences
                    </td>
                    <td className="py-3 px-2">1 year</td>
                  </tr>
                  <tr className="border-b border-white/5">
                    <td className="py-3 px-2">theme</td>
                    <td className="py-3 px-2">Stores your theme preference</td>
                    <td className="py-3 px-2">1 year</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-2">session</td>
                    <td className="py-3 px-2">Maintains your login state</td>
                    <td className="py-3 px-2">Session</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-6 rounded-2xl">
            <h3 className="text-lg font-bold text-white mb-4">
              Analytics Cookies
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              These cookies help us understand how visitors interact with our
              website.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-white/10 text-white font-semibold">
                    <th className="py-3 px-2">Cookie</th>
                    <th className="py-3 px-2">Purpose</th>
                    <th className="py-3 px-2">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-slate-400">
                  <tr className="border-b border-white/5">
                    <td className="py-3 px-2">_ga</td>
                    <td className="py-3 px-2">
                      Google Analytics - distinguishes users
                    </td>
                    <td className="py-3 px-2">2 years</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-2">_gid</td>
                    <td className="py-3 px-2">
                      Google Analytics - distinguishes users
                    </td>
                    <td className="py-3 px-2">24 hours</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-6 rounded-2xl">
            <h3 className="text-lg font-bold text-white mb-4">
              Marketing Cookies
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              These cookies are used to deliver personalized advertisements.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-white/10 text-white font-semibold">
                    <th className="py-3 px-2">Cookie</th>
                    <th className="py-3 px-2">Purpose</th>
                    <th className="py-3 px-2">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-slate-400">
                  <tr className="border-b border-white/5">
                    <td className="py-3 px-2">_fbp</td>
                    <td className="py-3 px-2">
                      Facebook - tracks visits across websites
                    </td>
                    <td className="py-3 px-2">3 months</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-2">_gcl_au</td>
                    <td className="py-3 px-2">
                      Google Ads - conversion tracking
                    </td>
                    <td className="py-3 px-2">3 months</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2>4. Managing Cookies</h2>
        <p>You can control and manage cookies in several ways:</p>
        <ul>
          <li>
            <strong>Cookie Banner:</strong> Use our cookie consent banner to
            accept or reject non-essential cookies
          </li>
          <li>
            <strong>Browser Settings:</strong> Most browsers allow you to block
            or delete cookies through their settings
          </li>
          <li>
            <strong>Opt-Out Tools:</strong> Use industry opt-out tools like{' '}
            <a
              href="https://optout.networkadvertising.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              NAI Opt-Out
            </a>
          </li>
        </ul>
        <p>
          Note: Disabling certain cookies may affect the functionality of our
          website.
        </p>
      </section>

      <section>
        <h2>5. Third-Party Cookies</h2>
        <p>
          Some cookies on our website are set by third-party services. We do not
          control these cookies. Please refer to the respective privacy policies
          of these third parties for more information about their cookies.
        </p>
      </section>

      <section>
        <h2>6. Updates to This Policy</h2>
        <p>
          We may update this Cookie Policy from time to time. Any changes will
          be posted on this page with an updated revision date. We encourage you
          to review this policy periodically.
        </p>
      </section>

      <section>
        <h2>7. Contact Us</h2>
        <p>
          If you have questions about our use of cookies, please contact us at:
        </p>
        <p>
          <strong>Email:</strong> privacy@controlstack.ai
        </p>
      </section>
    </LegalLayout>
  )
}
